import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Toast from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import articlesService from '../../services/articlesService';
import mediaService from '../../services/mediaService';
import circlesService from '../../services/circlesService';
import RichTextEditor from '../../components/editor/RichTextEditor';

const CreatorStudio = () => {
  const navigate = useNavigate();
  const { id: articleId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [contentMode, setContentMode] = useState('article');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState([{ sortOrder: 0, type: 'text', value: '' }]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [saving, setSaving] = useState(false);

  // Backend Integration States
  const [circles, setCircles] = useState([]);
  const [rootCircles, setRootCircles] = useState([]);
  const [poetrySubcategories, setPoetrySubcategories] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState('');
  const [circleDropdownOpen, setCircleDropdownOpen] = useState(false);
  const [poetryExpanded, setPoetryExpanded] = useState(false);
  const circleDropdownRef = useRef(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [reviewNote, setReviewNote] = useState(null);
  const [articleStatus, setArticleStatus] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);

  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const editorRef = useRef(null);
  const titleTextareaRef = useRef(null);
  const isDirtyRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const getFirstImageUrl = useCallback((blocks) => {
    const img = blocks?.find((c) => c.type === 'image' || c.type === 'Image');
    if (!img?.value) return null;
    try {
      const parsed = JSON.parse(img.value);
      return parsed?.url || img.value;
    } catch {
      return img.value;
    }
  }, []);

  const hasContentToSave = useCallback(() => {
    const hasTitle = title?.trim?.();
    const hasContent = content?.length > 1 || (content?.length === 1 && content[0]?.value?.trim?.());
    const hasCircle = !!selectedCircle;
    const hasCover = !!coverImage;
    return (hasTitle || hasContent || hasCover) && hasCircle;
  }, [title, content, selectedCircle, coverImage]);

  const buildDraftPayload = useCallback(() => ({
    title: title?.trim?.() || 'مسودة جديدة',
    content: content.map((c, idx) => ({ sortOrder: idx, type: c.type, value: c.value })),
    circleSlug: selectedCircle,
    coverImageUrl: coverImage || getFirstImageUrl(content) || null,
    type: contentMode === 'discussion' ? 'Discussion' : 'Article',
  }), [title, content, selectedCircle, coverImage, contentMode, getFirstImageUrl]);

  const saveDraft = useCallback(async (showToastOnSuccess = false) => {
    if (!hasContentToSave() && !articleId) return;
    if (loadingArticle) return;

    setSavingDraft(true);
    isDirtyRef.current = false;

    const payload = buildDraftPayload();

    try {
      if (articleId) {
        await articlesService.updateArticle(articleId, payload);
        setLastSavedAt(Date.now());
        if (showToastOnSuccess) {
          setToastMessage('تم حفظ المسودة');
          setToastVariant('success');
          setShowToast(true);
          setTimeout(() => navigate('/writer-dashboard?tab=drafts'), 1000);
        }
      } else {
        const { data: created } = await articlesService.createArticle(payload);
        setLastSavedAt(Date.now());
        if (showToastOnSuccess) {
          setToastMessage('تم حفظ المسودة');
          setToastVariant('success');
          setShowToast(true);
          setTimeout(() => navigate('/writer-dashboard?tab=drafts', { replace: true }), 1000);
        } else {
          navigate(`/creator-studio/${created.id}`, { replace: true });
        }
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      isDirtyRef.current = true;
      if (showToastOnSuccess) {
        setToastMessage('فشل حفظ المسودة');
        setToastVariant('error');
        setShowToast(true);
      }
    } finally {
      setSavingDraft(false);
    }
  }, [articleId, loadingArticle, hasContentToSave, buildDraftPayload, navigate]);

  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (isDirtyRef.current) saveDraft(false);
    }, 30000);
  }, [saveDraft]);

  useEffect(() => {
    if (articleId) isDirtyRef.current = false;
  }, [articleId]);

  useEffect(() => {
    if (loadingArticle) return;
    const handleVisibilityChange = () => {
      if (document.hidden && isDirtyRef.current) saveDraft(false);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadingArticle, saveDraft]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Auto-resize title textarea to fit content (no scrollbar)
  useEffect(() => {
    const ta = titleTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.max(80, ta.scrollHeight)}px`;
  }, [title, contentMode]);

  // Close circle dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (circleDropdownRef.current && !circleDropdownRef.current.contains(e.target)) {
        setCircleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    circlesService.getCircles()
      .then(({ data }) => {
        const raw = data || [];
        setCircles(raw);
        const idToSlug = raw.reduce((acc, c) => { acc[c.id] = c.slug; return acc; }, {});
        const mapped = raw.map(c => ({ ...c, parentSlug: c.parentId ? idToSlug[c.parentId] : null }));
        setRootCircles(mapped.filter(c => !c.parentId));
        setPoetrySubcategories(mapped.filter(c => c.parentSlug === 'poetry'));
      })
      .catch(console.error);
  }, []);

  // Load existing article when editing
  useEffect(() => {
    if (!articleId) return;

    setLoadingArticle(true);
    setIsEditMode(true);

    articlesService.getArticle(articleId)
      .then(({ data }) => {
        setTitle(data.title || '');
        const slug = data.circleSlug || '';
        setSelectedCircle(slug);
        if (['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(slug)) {
          setPoetryExpanded(true);
        }
        setCoverImage(data.image || null);
        setReviewNote(data.reviewNote || null);
        setArticleStatus(data.status || null);
        setContentMode(data.type?.toLowerCase() === 'discussion' ? 'discussion' : 'article');

        if (data.content && data.content.length > 0) {
          const blocks = data.content
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((block) => ({
              sortOrder: block.sortOrder,
              type: block.type,
              value: block.value
            }));
          setContent(blocks);
        }
      })
      .catch((err) => {
        console.error('Failed to load article:', err);
        setToastMessage('فشل تحميل المقال');
        setToastVariant('error');
        setShowToast(true);
        setTimeout(() => navigate('/writer-dashboard'), 2000);
      })
      .finally(() => {
        setLoadingArticle(false);
        isDirtyRef.current = false;
      });
  }, [articleId, navigate]);

  const uploadAndInsertImage = useCallback(async (file) => {
    try {
      const { data } = await mediaService.uploadImage(file);
      editorRef.current?.insertImage?.(data.url);
    } catch (err) {
      console.error(err);
      setToastMessage('فشل رفع الصورة');
      setToastVariant('error');
      setShowToast(true);
    }
  }, []);

  const uploadAndInsertAudio = useCallback(async (file) => {
    try {
      const { data } = await mediaService.uploadAudio(file);
      editorRef.current?.insertAudio?.(data.url);
    } catch (err) {
      console.error(err);
      setToastMessage('فشل رفع الملف الصوتي');
      setToastVariant('error');
      setShowToast(true);
    }
  }, []);

  const handleEditorImageRequest = useCallback((file) => {
    if (file) {
      uploadAndInsertImage(file);
    } else {
      fileInputRef.current?.click();
    }
  }, [uploadAndInsertImage]);

  const handleEditorAudioRequest = useCallback((file) => {
    if (file) {
      uploadAndInsertAudio(file);
    } else {
      audioInputRef.current?.click();
    }
  }, [uploadAndInsertAudio]);

  const handleEditorImageFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await uploadAndInsertImage(file);
  }, [uploadAndInsertImage]);

  const handleEditorAudioFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await uploadAndInsertAudio(file);
  }, [uploadAndInsertAudio]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverImageUploading(true);
    try {
      const { data } = await mediaService.uploadImage(file);
      setCoverImage(data.url);
      markDirty();
    } catch (error) {
      console.error("Cover upload failed", error);
      setToastMessage('فشل رفع صورة الغلاف');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setCoverImageUploading(false);
    }
  };

  const showErrorToast = (msg) => {
    setToastMessage(msg);
    setToastVariant('error');
    setShowToast(true);
  };

  const handlePublish = async () => {
    if (!title.trim()) return showErrorToast('الرجاء كتابة العنوان');
    if (!selectedCircle) return showErrorToast('الرجاء اختيار الدائرة');
    if (contentMode === 'article' && !coverImage) {
      const firstImg = getFirstImageUrl(content);
      if (!firstImg) return showErrorToast('الرجاء إضافة صورة غلاف للمقال');
    }

    setSaving(true);

    const finalCoverImage = coverImage || getFirstImageUrl(content) || null;

    const articlePayload = {
      title,
      content: content.map((c, idx) => ({
        sortOrder: idx,
        type: c.type,
        value: c.value
      })),
      circleSlug: selectedCircle,
      coverImageUrl: finalCoverImage,
      status: 'PUBLISHED',
      type: contentMode === 'discussion' ? 'Discussion' : 'Article'
    };

    try {
      if (isEditMode && articleId) {
        // Update the existing article, then resubmit for review
        await articlesService.updateArticle(articleId, articlePayload);
        await articlesService.publishArticle(articleId);
        setToastMessage('تم إعادة إرسال مقالك للمراجعة بنجاح.');
      } else {
        // Create new article, then submit for review
        const { data: created } = await articlesService.createArticle(articlePayload);
        await articlesService.publishArticle(created.id);
        setToastMessage('تم إرسال مقالك للمراجعة. سيتم إشعارك عند الموافقة.');
      }

      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => {
        navigate('/writer-dashboard');
      }, 1500);
    } catch (error) {
      console.error("Publishing failed", error);
      showErrorToast('حدث خطأ أثناء النشر');
    } finally {
      setSaving(false);
    }
  };

  const getSelectedCircleDisplayName = () => {
    if (!selectedCircle) return 'اختر الدائرة...';
    const fromRoot = rootCircles.find(c => c.slug === selectedCircle);
    if (fromRoot) return fromRoot.name;
    const fromSub = poetrySubcategories.find(c => c.slug === selectedCircle);
    return fromSub?.name || selectedCircle;
  };

  const titlePlaceholder = contentMode === 'article' ? 'العنوان' : 'ما السؤال الذي يدور في ذهنك؟';
  const bodyPlaceholder = contentMode === 'article' ? 'احكِ قصتك...' : 'أضف سياقاً للنقاش... (اختياري)';
  const submitButtonColor = contentMode === 'article' ? 'var(--color-success)' : '#2C2B29';

  let submitButtonText;
  if (saving) {
    submitButtonText = 'جاري الإرسال...';
  } else if (isEditMode) {
    submitButtonText = 'إعادة الإرسال للمراجعة';
  } else {
    submitButtonText = contentMode === 'article' ? 'إرسال للمراجعة' : 'إخلق الحلقة';
  }

  if (loadingArticle) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F7F2' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground" style={{ fontFamily: 'Amiri, serif' }}>جاري تحميل المقال...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F2' }}>
      {/* Hidden File Inputs for editor insert */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleEditorImageFileSelect}
        className="hidden"
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={handleEditorAudioFileSelect}
        className="hidden"
      />
      <input
        ref={coverInputRef}
        type="file"
        accept="image/*"
        onChange={handleCoverUpload}
        className="hidden"
      />

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-[var(--color-border)]">
        <div className="w-full max-w-6xl mx-auto px-6 py-4 flex justify-between items-center gap-8" dir="rtl">
          <div className="flex items-center gap-2 flex-shrink-0" ref={circleDropdownRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  const isOpen = !circleDropdownOpen;
                  setCircleDropdownOpen(isOpen);
                  if (isOpen && ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(selectedCircle)) {
                    setPoetryExpanded(true);
                  }
                }}
                className="bg-transparent text-sm border border-gray-300 rounded-md px-3 py-1.5 min-w-[160px] text-right flex items-center justify-between gap-2 focus:outline-none hover:border-gray-400 transition-colors"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                <span>{getSelectedCircleDisplayName()}</span>
                <Icon name={circleDropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="flex-shrink-0" />
              </button>
              {circleDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[100] max-h-[320px] overflow-y-auto min-w-[200px]" dir="rtl">
                  {rootCircles.map((c) => (
                    <div key={c.id}>
                      {c.slug === 'poetry' ? (
                        <>
                          <div className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-gray-50">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCircle(c.slug);
                                setCircleDropdownOpen(false);
                                markDirty();
                              }}
                              className={`flex-1 text-right ${selectedCircle === c.slug ? 'text-[#C5A059] font-medium' : ''}`}
                              style={{ fontFamily: 'Amiri, serif', fontSize: '14px' }}
                            >
                              {c.name}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPoetryExpanded(prev => !prev); }}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <Icon name={poetryExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                            </button>
                          </div>
                          {poetryExpanded && (
                            <div className="pr-4 border-r-2 border-[#C5A059]/30 mr-2">
                              {poetrySubcategories.map((sub) => (
                                <button
                                  key={sub.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCircle(sub.slug);
                                    setCircleDropdownOpen(false);
                                    markDirty();
                                  }}
                                  className={`w-full flex items-center gap-2 px-4 py-2 text-right hover:bg-gray-50 ${selectedCircle === sub.slug ? 'bg-[#C5A059]/10 text-[#C5A059] font-medium' : 'text-gray-700'}`}
                                  style={{ fontFamily: 'Amiri, serif', fontSize: '13px' }}
                                >
                                  {sub.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCircle(c.slug);
                            setCircleDropdownOpen(false);
                            markDirty();
                          }}
                          className={`w-full flex items-center gap-2 px-4 py-2.5 text-right hover:bg-gray-50 ${selectedCircle === c.slug ? 'bg-[#C5A059]/10 text-[#C5A059]' : ''}`}
                          style={{ fontFamily: 'Amiri, serif', fontSize: '14px' }}
                        >
                          {c.name}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cover Image Upload Button */}
            <button
              onClick={() => coverInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors text-sm"
              title="تحميل صورة الغلاف"
            >
              <Icon name="Image" size={16} />
              <span style={{ fontFamily: 'Amiri, serif' }}>
                {coverImage ? 'تغيير الغلاف' : 'صورة الغلاف'}
              </span>
            </button>
            {coverImageUploading && <span className="text-xs text-gray-500">جاري الرفع...</span>}
          </div>

          <div className="flex-1 flex items-center min-w-0">
            <div className="flex items-center gap-6">
              <button onClick={() => { setContentMode('article'); markDirty(); }} className={`flex items-center gap-2 transition-all ${contentMode === 'article' ? 'font-bold' : ''}`} style={{ fontFamily: 'Aref Ruqaa, serif', fontSize: '18px', color: contentMode === 'article' ? '#C5A059' : '#9CA3AF' }}>
                {contentMode === 'article' && <span style={{ color: '#C5A059' }}>●</span>}
                مقال
              </button>
              <button onClick={() => { setContentMode('discussion'); markDirty(); }} className={`flex items-center gap-2 transition-all ${contentMode === 'discussion' ? 'font-bold' : ''}`} style={{ fontFamily: 'Aref Ruqaa, serif', fontSize: '18px', color: contentMode === 'discussion' ? '#C5A059' : '#9CA3AF' }}>
                {contentMode === 'discussion' && <span style={{ color: '#C5A059' }}>●</span>}
                حلقة نقاش
              </button>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={() => {
                  if (!selectedCircle) {
                    showErrorToast('الرجاء اختيار الدائرة');
                    return;
                  }
                  saveDraft(true);
                }}
                disabled={saving || savingDraft}
                className="rounded-full px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                {savingDraft ? 'جاري الحفظ...' : 'حفظ المسودة'}
              </button>
              <Button onClick={handlePublish} disabled={saving} className="rounded-full px-6 py-2 text-sm flex-shrink-0" style={{ fontFamily: 'Amiri, serif', backgroundColor: submitButtonColor, color: 'white', opacity: saving ? 0.7 : 1 }}>
                {submitButtonText}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Cover Image Preview */}
      <main className="pt-24 pb-16">
        <div className="max-w-[740px] mx-auto px-6">

          {/* Review Note Banner */}
          {isEditMode && reviewNote && (
            <div className="mb-6 p-4 rounded-xl border-2 border-amber-300 bg-amber-50" dir="rtl">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Icon name="AlertTriangle" size={22} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-amber-800 mb-1" style={{ fontFamily: 'Amiri, serif' }}>
                    {articleStatus === 'Rejected' ? 'تم رفض المقال' : 'مطلوب تعديلات'}
                  </h3>
                  <p className="text-sm text-amber-700 leading-relaxed" style={{ fontFamily: 'Amiri, serif' }}>
                    {reviewNote}
                  </p>
                  <p className="text-xs text-amber-500 mt-2" style={{ fontFamily: 'Amiri, serif' }}>
                    قم بإجراء التعديلات المطلوبة ثم اضغط &quot;إعادة الإرسال للمراجعة&quot;
                  </p>
                </div>
              </div>
            </div>
          )}

          {coverImage && (
            <div className="mb-8 relative w-full h-[300px] rounded-xl overflow-hidden shadow-md group">
              <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
              <button
                onClick={() => { setCoverImage(null); markDirty(); }}
                className="absolute top-2 right-2 bg-white/80 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div className="mt-8">
            <RichTextEditor
              ref={editorRef}
              contentBlocks={content}
              onChange={(blocks) => { setContent(blocks); markDirty(); }}
              onRequestInsertImage={handleEditorImageRequest}
              onRequestInsertAudio={handleEditorAudioRequest}
              placeholder={bodyPlaceholder}
              titleSlot={
                <textarea
                  ref={titleTextareaRef}
                  value={title}
                  onChange={(e) => { setTitle(e?.target?.value); markDirty(); }}
                  placeholder={titlePlaceholder}
                  rows={1}
                  className="w-full min-h-[80px] px-0 py-4 bg-transparent resize-none focus:outline-none mt-4 overflow-hidden"
                  style={{ fontFamily: 'Amiri, serif', fontWeight: 700, fontSize: contentMode === 'discussion' ? '42px' : '48px', lineHeight: '1.2', color: '#2C2B29', direction: 'rtl', border: 'none', boxShadow: 'none' }}
                />
              }
            />
          </div>
        </div>
      </main>

      {showToast && (
        <Toast message={toastMessage} onClose={() => setShowToast(false)} duration={3000} variant={toastVariant} />
      )}
    </div>
  );
};

export default CreatorStudio;
